import type { DraftState, Manager, TeamLiveData } from '../types';
import { TEAMS_BY_ABBR, teamFullName } from '../constants/teams';
import { BASELINE_PROJECTIONS } from '../constants/teamProjections';
import { maxBidPower, nextBidderId } from './draftLogic';
import type { TeamValuation } from './valuation';

export type AIPlaystyle = 'conservative' | 'balanced' | 'aggressive';

export interface LLMConfig {
  provider: 'builtin' | 'openai' | 'anthropic' | 'gemini' | 'openrouter';
  apiKey?: string;
  model?: string;
}

export interface AdvisorSettings {
  managerId: string;
  playstyle: AIPlaystyle;
  priceEnforcing: boolean;
  llmConfig: LLMConfig;
}

export const DEFAULT_ADVISOR_SETTINGS: AdvisorSettings = {
  managerId: 'rob',
  playstyle: 'balanced',
  priceEnforcing: true,
  llmConfig: {
    provider: 'builtin',
  },
};

const SETTINGS_STORAGE_KEY = 'nfl_draft_ai_advisor_settings_v1';

export function loadAdvisorSettings(): AdvisorSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return { ...DEFAULT_ADVISOR_SETTINGS, ...parsed };
    }
  } catch {}
  return DEFAULT_ADVISOR_SETTINGS;
}

export function saveAdvisorSettings(settings: AdvisorSettings) {
  try {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {}
}

export interface OpponentIntel {
  manager: Manager;
  slotsRemaining: number;
  maxBid: number;
  avgBudgetPerSlot: number;
  spendingPower: 'WHALE' | 'HEALTHY' | 'CONSTRAINED' | 'CRITICAL';
  isVulnerable: boolean;
  threatLevel: 'HIGH' | 'MEDIUM' | 'LOW';
  vulnerabilityNote: string;
}

export interface BidDecision {
  shouldBid: boolean;
  action: 'STRONG_BUY' | 'VALUE_BUY' | 'PRICE_ENFORCE' | 'PASS_OVERPRICED' | 'PASS_BUDGET' | 'HOLD_HIGH_BID' | 'CANNOT_AFFORD';
  recommendedBid: number;
  maxProfitableBid: number;
  fairMarketValue: number;
  surplusValue: number;
  confidence: number; // 0..100
  headline: string;
  tacticalReason: string;
  keyPoints: string[];
  riskLevel: 'LOW' | 'MODERATE' | 'HIGH';
  isWorthThePrice: boolean;
  winsPerDollar: number;
  portfolioFit: {
    score: number; // 0..100
    conferenceBalance: string;
    divisionClash: boolean;
    note: string;
  };
  opponentContext: string;
}

export interface NominationOption {
  team: string;
  teamName: string;
  recommendedBid: number;
  strategy: 'VALUE_TARGET' | 'DRAIN_RIVALS' | 'CAP_SQUEEZE' | 'POWER_ANCHOR' | 'BARGAIN_SLEEPER';
  strategyTitle: string;
  tagline: string;
  fairValue: number;
  projectedWins: number;
  tier: number;
  tacticalRationale: string;
  primaryTargetRival?: string;
  score: number;
}

export interface NominationAdvice {
  primary: NominationOption;
  alternatives: NominationOption[];
  marketContext: string;
}

/** Opponent modeling across the league */
export function analyzeOpponents(state: DraftState, myId: string): OpponentIntel[] {
  return state.managers
    .filter(m => m.id !== myId)
    .map(m => {
      const slots = Math.max(0, state.rosterCap - m.teamsDrafted);
      const maxBid = maxBidPower(state, m.id);
      const avg = slots > 0 ? (m.budget / slots) : 0;

      let spendingPower: OpponentIntel['spendingPower'] = 'HEALTHY';
      if (avg >= 9 && maxBid >= 20) spendingPower = 'WHALE';
      else if (avg <= 2 || maxBid <= 2) spendingPower = 'CRITICAL';
      else if (avg <= 4.5 || maxBid <= 7) spendingPower = 'CONSTRAINED';

      const isVulnerable = spendingPower === 'CRITICAL' || spendingPower === 'CONSTRAINED';
      const threatLevel: OpponentIntel['threatLevel'] = maxBid >= 18 ? 'HIGH' : maxBid >= 8 ? 'MEDIUM' : 'LOW';

      let vulnerabilityNote = `${m.name} has $${m.budget} left across ${slots} slot${slots === 1 ? '' : 's'}.`;
      if (slots <= 0) {
        vulnerabilityNote = `Roster full (0 slots left). Cannot bid.`;
      } else if (maxBid <= 2) {
        vulnerabilityNote = `Cap squeezed! Max single bid is only $${maxBid}. Easy to outbid.`;
      } else if (spendingPower === 'CONSTRAINED') {
        vulnerabilityNote = `Budget constrained: Must average $${avg.toFixed(1)}/slot. Won't survive a bidding war.`;
      } else if (spendingPower === 'WHALE') {
        vulnerabilityNote = `Dangerous stack! Can bid up to $${maxBid} ($${avg.toFixed(1)}/slot avg).`;
      }

      return {
        manager: m,
        slotsRemaining: slots,
        maxBid,
        avgBudgetPerSlot: avg,
        spendingPower,
        isVulnerable,
        threatLevel,
        vulnerabilityNote,
      };
    });
}

/** Get best projected wins for a team combining live data & 2026 baseline */
export function getEffectiveTeamProjections(
  abbr: string,
  liveData: Record<string, TeamLiveData>,
) {
  const live = liveData[abbr];
  const base = BASELINE_PROJECTIONS[abbr];

  const projectedWins = live?.projectedWins ?? base?.projectedWins ?? 8.5;
  const fpi = live?.fpi ?? base?.fpi ?? 0;
  const fpiRank = live?.fpiRank ?? base?.fpiRank ?? 16;
  const sosRank = live?.sosRank ?? base?.sosRank ?? 16;
  const byeWeek = base?.byeWeek ?? 9;
  const tier = base?.tier ?? 3;
  const keyStars = (live?.keyPlayers?.length ? live.keyPlayers.map(p => `${p.name} (${p.position})`) : null) ?? base?.keyStars ?? [];

  return {
    abbr,
    projectedWins,
    fpi,
    fpiRank,
    sosRank,
    byeWeek,
    tier,
    keyStars,
    notes: base?.notes ?? 'Solid team with high upside potential.',
    meta: TEAMS_BY_ABBR[abbr],
  };
}

/** Evaluate portfolio synergy for Rob's current roster */
function computePortfolioSynergy(
  teamAbbr: string,
  myTeams: string[],
): { score: number; conferenceBalance: string; divisionClash: boolean; note: string } {
  const meta = TEAMS_BY_ABBR[teamAbbr];
  if (!meta) {
    return { score: 75, conferenceBalance: 'Neutral', divisionClash: false, note: 'Standard roster addition.' };
  }

  const existing = myTeams.map(a => TEAMS_BY_ABBR[a]).filter(Boolean);
  const afcCount = existing.filter(t => t.conference === 'AFC').length;
  const nfcCount = existing.filter(t => t.conference === 'NFC').length;

  const sameDivision = existing.some(t => t.conference === meta.conference && t.division === meta.division);

  let score = 80;
  let note = 'Well balanced addition to your portfolio.';

  if (sameDivision) {
    score -= 25;
    note = `Warning: Division clash with ${existing.find(t => t.conference === meta.conference && t.division === meta.division)?.abbr}. Head-to-head division matchups will cannibalize wins.`;
  } else if (meta.conference === 'AFC' && afcCount >= nfcCount + 2) {
    score -= 10;
    note = `Roster is heavily AFC-tilted (${afcCount} AFC vs ${nfcCount} NFC). NFC targets offer better conference diversification.`;
  } else if (meta.conference === 'NFC' && nfcCount >= afcCount + 2) {
    score -= 10;
    note = `Roster is heavily NFC-tilted (${nfcCount} NFC vs ${afcCount} AFC). AFC targets offer better conference diversification.`;
  } else {
    score += 10;
    note = `Excellent conference diversification (${afcCount} AFC / ${nfcCount} NFC).`;
  }

  const balance = `${afcCount + (meta.conference === 'AFC' ? 1 : 0)} AFC / ${nfcCount + (meta.conference === 'NFC' ? 1 : 0)} NFC`;
  return {
    score: Math.max(0, Math.min(100, score)),
    conferenceBalance: balance,
    divisionClash: sameDivision,
    note,
  };
}

/**
 * CORE AI BID EVALUATION ENGINE
 * Tells Rob whether to bid, whether the team is worth the price, and max profitable ceiling.
 */
export function evaluateBidOpportunity(
  state: DraftState,
  managerId: string,
  team: string,
  currentHighBid: number,
  highBidderId: string | null,
  liveData: Record<string, TeamLiveData>,
  valuations: Record<string, TeamValuation>,
  settings: AdvisorSettings = DEFAULT_ADVISOR_SETTINGS,
): BidDecision {
  const me = state.managers.find(m => m.id === managerId);
  const power = me ? maxBidPower(state, me.id) : 0;
  const slotsRemaining = me ? Math.max(0, state.rosterCap - me.teamsDrafted) : 0;
  const nextRequiredBid = currentHighBid + 1;
  const isMyTurn = me ? nextBidderId(state) === me.id : false;
  const isHighBidder = me?.id === highBidderId;

  const proj = getEffectiveTeamProjections(team, liveData);
  const valuation = valuations[team];
  const baseSuggested = valuation?.suggested ?? Math.round(proj.projectedWins * 1.2);

  // Dynamic Fair Market Value (FMV) adjusted for league cash scarcity and aggression
  const remainingLeagueBudget = state.managers.reduce(
    (acc, m) => acc + Math.max(0, m.budget - Math.max(0, state.rosterCap - m.teamsDrafted - 1) * state.minBid),
    0,
  );
  const remainingSlots = state.managers.reduce(
    (acc, m) => acc + Math.max(0, state.rosterCap - m.teamsDrafted),
    0,
  );
  const marketInflationRatio = remainingSlots > 0 ? (remainingLeagueBudget / remainingSlots) / 6.5 : 1;

  let aggressionMult = 1.0;
  if (settings.playstyle === 'aggressive') aggressionMult = 1.15;
  if (settings.playstyle === 'conservative') aggressionMult = 0.88;

  // Fair Market Value (FMV)
  const fairMarketValue = Math.max(state.minBid, Math.round(baseSuggested * marketInflationRatio * aggressionMult));

  // Max profitable bid before negative expected ROI
  // Rob should never bid higher than what allows him to fill his remaining slots with $1 each
  const maxProfitableBid = Math.min(
    power,
    Math.max(state.minBid, fairMarketValue + (settings.playstyle === 'aggressive' ? 2 : 0))
  );

  const surplusValue = fairMarketValue - currentHighBid;
  const isWorthThePrice = currentHighBid <= fairMarketValue;
  const winsPerDollar = currentHighBid > 0 ? Number((proj.projectedWins / currentHighBid).toFixed(2)) : proj.projectedWins;

  const opponents = analyzeOpponents(state, managerId);
  const topOpponentMax = opponents.length ? Math.max(...opponents.map(o => o.maxBid)) : 0;
  const activeOpponents = opponents.filter(o => o.slotsRemaining > 0 && o.maxBid >= nextRequiredBid);
  const portfolioFit = computePortfolioSynergy(team, me?.teams ?? []);

  // Opponent context note
  let opponentContext = 'No serious bidding threats remaining.';
  if (activeOpponents.length > 0) {
    const highestThreat = opponents.slice().sort((a, b) => b.maxBid - a.maxBid)[0];
    opponentContext = `Highest rival threat: ${highestThreat.manager.name} (can bid up to $${highestThreat.maxBid}).`;
  }

  // Already high bidder: HOLD
  if (isHighBidder) {
    return {
      shouldBid: false,
      action: 'HOLD_HIGH_BID',
      recommendedBid: currentHighBid,
      maxProfitableBid,
      fairMarketValue,
      surplusValue,
      confidence: 95,
      headline: `HOLD — You are the High Bidder at $${currentHighBid}`,
      tacticalReason: `You are in the driver's seat. Let the shot clock run down or wait for opponents to overpay. Do not raise against yourself.`,
      keyPoints: [
        `Current Bid: $${currentHighBid} (Fair Value: $${fairMarketValue})`,
        `Surplus if won now: +$${surplusValue} value equity`,
        `Opponents still must beat $${currentHighBid}`,
      ],
      riskLevel: 'LOW',
      isWorthThePrice: true,
      winsPerDollar,
      portfolioFit,
      opponentContext,
    };
  }

  // Cannot afford next required bid
  if (power < nextRequiredBid) {
    return {
      shouldBid: false,
      action: 'CANNOT_AFFORD',
      recommendedBid: 0,
      maxProfitableBid,
      fairMarketValue,
      surplusValue,
      confidence: 99,
      headline: `PASS — Insufficient Bidding Power`,
      tacticalReason: `Your max allowable bid is $${power}, which is below the next required bid ($${nextRequiredBid}). You must pass.`,
      keyPoints: [
        `Your max bid: $${power}`,
        `Next required bid: $${nextRequiredBid}`,
        `Preserve remaining $${me?.budget ?? 0} for future open slots`,
      ],
      riskLevel: 'HIGH',
      isWorthThePrice,
      winsPerDollar,
      portfolioFit,
      opponentContext,
    };
  }

  // Check if team is overpriced
  if (nextRequiredBid > maxProfitableBid) {
    const overpayDelta = nextRequiredBid - fairMarketValue;
    return {
      shouldBid: false,
      action: 'PASS_OVERPRICED',
      recommendedBid: 0,
      maxProfitableBid,
      fairMarketValue,
      surplusValue,
      confidence: 92,
      headline: `PASS — Overpriced by +$${overpayDelta}`,
      tacticalReason: `At $${nextRequiredBid}, ${teamFullName(team)} exceeds your maximum profitable value ceiling ($${fairMarketValue}). Let your opponents burn their budget here!`,
      keyPoints: [
        `Current price ($${currentHighBid}) > Fair Value ($${fairMarketValue})`,
        `Wins/$ efficiency drops to ${winsPerDollar} W/$`,
        `Passing forces opponents to tie up their limited capital`,
      ],
      riskLevel: 'HIGH',
      isWorthThePrice: false,
      winsPerDollar,
      portfolioFit,
      opponentContext,
    };
  }

  // Price enforcing tactic: If Rob doesn't desperately want the team, but opponents are getting a massive steal
  const isExtremeStealForOpponent = surplusValue >= 4 && activeOpponents.length > 0;
  if (settings.priceEnforcing && isExtremeStealForOpponent && nextRequiredBid < fairMarketValue - 1 && power >= nextRequiredBid + 2) {
    return {
      shouldBid: true,
      action: 'PRICE_ENFORCE',
      recommendedBid: nextRequiredBid,
      maxProfitableBid,
      fairMarketValue,
      surplusValue,
      confidence: 86,
      headline: `BID $${nextRequiredBid} — Price Enforce & Trap Rivals`,
      tacticalReason: `Rivals are getting ${teamFullName(team)} at a massive discount (Fair Value: $${fairMarketValue}). Bump to $${nextRequiredBid} to force them to spend or win an undervalued asset.`,
      keyPoints: [
        `Deep value: $${fairMarketValue} value for only $${nextRequiredBid}`,
        `Forces ${activeOpponents[0]?.manager.name ?? 'opponents'} to spend their capital`,
        `Safety net: If you win at $${nextRequiredBid}, you lock in a +$${fairMarketValue - nextRequiredBid} bargain!`,
      ],
      riskLevel: 'MODERATE',
      isWorthThePrice: true,
      winsPerDollar,
      portfolioFit,
      opponentContext,
    };
  }

  // Value Buy / Strong Buy
  if (surplusValue >= 3) {
    // Large surplus: Strong Buy
    return {
      shouldBid: true,
      action: 'STRONG_BUY',
      recommendedBid: nextRequiredBid,
      maxProfitableBid,
      fairMarketValue,
      surplusValue,
      confidence: 94,
      headline: `BID $${nextRequiredBid} — High Priority Target (+$${surplusValue} Surplus)`,
      tacticalReason: `${teamFullName(team)} is one of the highest value plays on the board. Projected ${proj.projectedWins.toFixed(1)} wins at only $${nextRequiredBid} is elite ROI. Bid now!`,
      keyPoints: [
        `Fair Value: $${fairMarketValue} (Current: $${currentHighBid})`,
        `Projected wins: ${proj.projectedWins.toFixed(1)} (${proj.tier === 1 ? 'Tier 1 Contender' : 'High Floor'})`,
        `Portfolio synergy: ${portfolioFit.score}/100 (${portfolioFit.note})`,
        `You have bidding leverage up to $${maxProfitableBid}`,
      ],
      riskLevel: 'LOW',
      isWorthThePrice: true,
      winsPerDollar,
      portfolioFit,
      opponentContext,
    };
  }

  // Standard Value Buy (Within fair value)
  return {
    shouldBid: true,
    action: 'VALUE_BUY',
    recommendedBid: nextRequiredBid,
    maxProfitableBid,
    fairMarketValue,
    surplusValue,
    confidence: 84,
    headline: `BID $${nextRequiredBid} — Solid Market Value`,
    tacticalReason: `${teamFullName(team)} is fairly priced at $${nextRequiredBid} against a $${fairMarketValue} market value. Keep bidding up to your $${maxProfitableBid} walk-away ceiling.`,
    keyPoints: [
      `Next bid: $${nextRequiredBid} (Fair Value: $${fairMarketValue})`,
      `Efficiency: ${winsPerDollar} projected wins per dollar`,
      `Walk-away price ceiling: $${maxProfitableBid}`,
    ],
    riskLevel: 'LOW',
    isWorthThePrice: true,
    winsPerDollar,
    portfolioFit,
    opponentContext,
  };
}

/**
 * CORE AI NOMINATION ENGINE
 * Analyzes the board and recommends which team Rob should nominate and for how much.
 */
export function evaluateNominationOptions(
  state: DraftState,
  managerId: string,
  liveData: Record<string, TeamLiveData>,
  valuations: Record<string, TeamValuation>,
  pastPrices: Record<string, number>,
  settings: AdvisorSettings = DEFAULT_ADVISOR_SETTINGS,
): NominationAdvice {
  const me = state.managers.find(m => m.id === managerId);
  const myPower = me ? maxBidPower(state, me.id) : 0;
  const mySlots = me ? Math.max(0, state.rosterCap - me.teamsDrafted) : 0;
  const opponents = analyzeOpponents(state, managerId);
  const available = state.availableTeams;

  // Score every available team across nomination strategies
  const analyzed = available.map(abbr => {
    const proj = getEffectiveTeamProjections(abbr, liveData);
    const val = valuations[abbr];
    const past = pastPrices[abbr] ?? val?.leagueAvgPrior ?? 8;
    const fairVal = val?.suggested ?? Math.round(proj.projectedWins * 1.2);
    const fit = computePortfolioSynergy(abbr, me?.teams ?? []);

    return {
      abbr,
      meta: TEAMS_BY_ABBR[abbr],
      proj,
      fairVal,
      past,
      fit,
    };
  });

  const options: NominationOption[] = [];

  // 1. "DRAIN_RIVALS" Strategy:
  // Pick an expensive, high-prestige team (Tier 1/2) that Rob doesn't need or has division clash with,
  // to force wealthy opponents (David, Frank, Ryan) to burn their bankroll.
  const drainCandidates = analyzed
    .filter(t => t.proj.tier <= 2 && t.fairVal >= 10 && (t.fit.divisionClash || t.fit.score < 80 || settings.playstyle === 'aggressive'))
    .sort((a, b) => b.fairVal - a.fairVal);

  if (drainCandidates.length > 0) {
    const topDrain = drainCandidates[0];
    const primeRival = opponents.slice().sort((a, b) => b.maxBid - a.maxBid)[0];
    options.push({
      team: topDrain.abbr,
      teamName: teamFullName(topDrain.abbr),
      recommendedBid: state.minBid, // Start at $1 so opponents start bidding war
      strategy: 'DRAIN_RIVALS',
      strategyTitle: '🔥 Opponent Wallet Drainer',
      tagline: `Nominate ${topDrain.abbr} at $${state.minBid} to burn rivals' cash`,
      fairValue: topDrain.fairVal,
      projectedWins: topDrain.proj.projectedWins,
      tier: topDrain.proj.tier,
      tacticalRationale: `${teamFullName(topDrain.abbr)} is a marquee name everyone will bid on. Nominate at $${state.minBid} and let ${primeRival ? primeRival.manager.name : 'opponents'} drain their remaining budget in a bidding war while you sit back!`,
      primaryTargetRival: primeRival?.manager.name,
      score: 95,
    });
  }

  // 2. "VALUE_TARGET" Strategy:
  // High efficiency team that Rob wants to steal at $1 or low bid.
  const valueCandidates = analyzed
    .filter(t => !t.fit.divisionClash && t.fit.score >= 75)
    .sort((a, b) => {
      const aSurplus = a.fairVal - Math.min(a.past, a.fairVal);
      const bSurplus = b.fairVal - Math.min(b.past, b.fairVal);
      return bSurplus - aSurplus || b.proj.projectedWins - a.proj.projectedWins;
    });

  if (valueCandidates.length > 0) {
    const topValue = valueCandidates[0];
    options.push({
      team: topValue.abbr,
      teamName: teamFullName(topValue.abbr),
      recommendedBid: state.minBid,
      strategy: 'VALUE_TARGET',
      strategyTitle: '🎯 Stealth Value Snipe',
      tagline: `Nominate ${topValue.abbr} at $${state.minBid} to snag under market price`,
      fairValue: topValue.fairVal,
      projectedWins: topValue.proj.projectedWins,
      tier: topValue.proj.tier,
      tacticalRationale: `${teamFullName(topValue.abbr)} is projected for ${topValue.proj.projectedWins.toFixed(1)} wins (Fair Value: $${topValue.fairVal}). Nominate at $${state.minBid} to test if opponents are sleeping and sneak it through cheap.`,
      score: 92,
    });
  }

  // 3. "CAP_SQUEEZE" Strategy:
  // If an opponent has very few dollars left (e.g. max bid <= 6), nominate a solid mid-tier team to force them all-in.
  const squeezedOpponents = opponents.filter(o => o.isVulnerable && o.slotsRemaining > 0);
  if (squeezedOpponents.length > 0) {
    const targetOpponent = squeezedOpponents[0];
    const squeezeCandidates = analyzed
      .filter(t => t.fairVal >= targetOpponent.maxBid - 2 && t.fairVal <= targetOpponent.maxBid + 4)
      .sort((a, b) => b.proj.projectedWins - a.proj.projectedWins);

    if (squeezeCandidates.length > 0) {
      const topSqueeze = squeezeCandidates[0];
      options.push({
        team: topSqueeze.abbr,
        teamName: teamFullName(topSqueeze.abbr),
        recommendedBid: state.minBid,
        strategy: 'CAP_SQUEEZE',
        strategyTitle: '⚡ Cap Squeeze Trap',
        tagline: `Squeeze ${targetOpponent.manager.name}'s remaining $${targetOpponent.manager.budget}`,
        fairValue: topSqueeze.fairVal,
        projectedWins: topSqueeze.proj.projectedWins,
        tier: topSqueeze.proj.tier,
        tacticalRationale: `${targetOpponent.manager.name} only has $${targetOpponent.manager.budget} left across ${targetOpponent.slotsRemaining} slots. Nominating ${teamFullName(topSqueeze.abbr)} forces them to either spend their last flexible dollar or forfeit.`,
        primaryTargetRival: targetOpponent.manager.name,
        score: 88,
      });
    }
  }

  // 4. "POWER_ANCHOR" Strategy:
  // Elite Tier 1 team Rob can secure if he has strong budget.
  const powerCandidates = analyzed
    .filter(t => t.proj.tier === 1 && !t.fit.divisionClash && myPower >= t.fairVal)
    .sort((a, b) => b.proj.projectedWins - a.proj.projectedWins);

  if (powerCandidates.length > 0) {
    const topPower = powerCandidates[0];
    options.push({
      team: topPower.abbr,
      teamName: teamFullName(topPower.abbr),
      recommendedBid: state.minBid,
      strategy: 'POWER_ANCHOR',
      strategyTitle: '👑 Cornerstone Anchor',
      tagline: `Anchor your roster with powerhouse ${topPower.abbr}`,
      fairValue: topPower.fairVal,
      projectedWins: topPower.proj.projectedWins,
      tier: 1,
      tacticalRationale: `Lock down a league-winning cornerstone. ${teamFullName(topPower.abbr)} brings ${topPower.proj.projectedWins.toFixed(1)} projected wins and elite playoff upside.`,
      score: 85,
    });
  }

  // 5. "BARGAIN_SLEEPER" Strategy:
  const sleeperCandidates = analyzed
    .filter(t => t.proj.tier >= 3 && t.fairVal <= 8 && t.proj.projectedWins >= 8.2 && !t.fit.divisionClash)
    .sort((a, b) => (b.proj.projectedWins / Math.max(1, b.fairVal)) - (a.proj.projectedWins / Math.max(1, a.fairVal)));

  if (sleeperCandidates.length > 0) {
    const topSleeper = sleeperCandidates[0];
    options.push({
      team: topSleeper.abbr,
      teamName: teamFullName(topSleeper.abbr),
      recommendedBid: state.minBid,
      strategy: 'BARGAIN_SLEEPER',
      strategyTitle: '💎 High-Yield Sleeper',
      tagline: `High ROI sleeper: ${topSleeper.abbr} for ~$${topSleeper.fairVal}`,
      fairValue: topSleeper.fairVal,
      projectedWins: topSleeper.proj.projectedWins,
      tier: topSleeper.proj.tier,
      tacticalRationale: `${teamFullName(topSleeper.abbr)} offers huge value efficiency (${(topSleeper.proj.projectedWins / Math.max(1, topSleeper.fairVal)).toFixed(2)} W/$). Great way to lock in wins while protecting your budget.`,
      score: 83,
    });
  }

  // Ensure at least one option exists
  if (options.length === 0 && available.length > 0) {
    const fallback = analyzed[0];
    options.push({
      team: fallback.abbr,
      teamName: teamFullName(fallback.abbr),
      recommendedBid: state.minBid,
      strategy: 'VALUE_TARGET',
      strategyTitle: '🎯 Available Target',
      tagline: `Nominate ${fallback.abbr}`,
      fairValue: fallback.fairVal,
      projectedWins: fallback.proj.projectedWins,
      tier: fallback.proj.tier,
      tacticalRationale: `Solid pick to keep the auction moving.`,
      score: 70,
    });
  }

  // Sort by score
  options.sort((a, b) => b.score - a.score);

  const primary = options[0];
  const alternatives = options.slice(1, 4);

  const marketContext = `Remaining pool: ${available.length} teams. Your budget: $${me?.budget ?? 0} across ${mySlots} slots.`;

  return {
    primary,
    alternatives,
    marketContext,
  };
}

/**
 * On-demand LLM Scouting Report generator
 * If API key is provided, calls external LLM (OpenAI, Anthropic, Gemini, OpenRouter).
 * Otherwise generates an immediate, deterministic, high-intelligence tactical report.
 */
export async function generateLLMScoutingReport(
  promptContext: {
    managerName: string;
    teamAbbr?: string;
    state: DraftState;
    liveData: Record<string, TeamLiveData>;
    valuations: Record<string, TeamValuation>;
  },
  settings: AdvisorSettings,
): Promise<string> {
  const { managerName, teamAbbr, state, liveData, valuations } = promptContext;

  // If user configured an external key, we can call it:
  if (settings.llmConfig?.apiKey && settings.llmConfig.provider !== 'builtin') {
    try {
      const response = await callExternalLLM(promptContext, settings);
      if (response) return response;
    } catch (e: any) {
      console.warn('External LLM call failed, falling back to local reasoning:', e);
    }
  }

  // High-fidelity local AI reasoning engine
  if (teamAbbr) {
    const proj = getEffectiveTeamProjections(teamAbbr, liveData);
    const val = valuations[teamAbbr];
    const decision = evaluateBidOpportunity(
      state,
      settings.managerId,
      teamAbbr,
      state.auction.highBid,
      state.auction.highBidderId,
      liveData,
      valuations,
      settings
    );

    return `### 🏈 AI Tactical Dossier: ${teamFullName(teamAbbr)} (${teamAbbr})
**Commissioner Advisory for ${managerName}**

- **Projected Season Wins:** ${proj.projectedWins.toFixed(1)} wins (FPI Rank: #${proj.fpiRank})
- **Fair Market Value:** $${decision.fairMarketValue} (Suggested Base: $${val?.suggested ?? decision.fairMarketValue})
- **Maximum Walk-Away Price:** $${decision.maxProfitableBid}
- **Value Efficiency:** ${decision.winsPerDollar} Wins per Dollar
- **Key Stars:** ${proj.keyStars.join(', ') || 'Team core roster'}

#### Strategic Verdict:
${decision.headline}
${decision.tacticalReason}

#### Portfolio & Opponent Dynamics:
- **Roster Synergy:** ${decision.portfolioFit.note}
- **League Pressure:** ${decision.opponentContext}
- **Recommended Action:** ${decision.shouldBid ? `Bid $${decision.recommendedBid} immediately` : 'Hold or Pass to force opponents into budget fatigue'}.`;
  }

  // Full league draft summary
  const me = state.managers.find(m => m.id === settings.managerId);
  const opponents = analyzeOpponents(state, settings.managerId);
  const topVulnerable = opponents.filter(o => o.isVulnerable);

  return `### 🏆 Draft State Strategic Assessment: Rob's AI Command
**Season ${state.currentDraftYear} · NFL Fantasy Team Auction**

#### 1. Your Roster Architecture
- **Capital Remaining:** $${me?.budget ?? 0} with ${Math.max(0, state.rosterCap - (me?.teamsDrafted ?? 0))} roster slots left.
- **Current Teams:** ${me?.teams.length ? me.teams.map(t => teamFullName(t)).join(', ') : 'No teams drafted yet (Maximum flexibility)'}.

#### 2. Opponent Threat & Vulnerability Matrix
${opponents.map(o => `- **${o.manager.name}:** $${o.manager.budget} left (${o.slotsRemaining} slots) — Max bid $${o.maxBid}. ${o.vulnerabilityNote}`).join('\n')}

#### 3. AI Game Theory Directive:
- **Price Enforcing:** ${settings.priceEnforcing ? 'ACTIVE — Do not let marquee teams go for under 80% of Fair Market Value.' : 'PASSIVE — Focus strictly on value snipes.'}
- **Opponent Traps:** ${topVulnerable.length ? `${topVulnerable.map(v => v.manager.name).join(' and ')} are entering critical cap strain. Nominate Tier 2 teams to force them into bad spots.` : 'Room still has liquidity; focus on draining top stacks.'}`;
}

async function callExternalLLM(
  context: {
    managerName: string;
    teamAbbr?: string;
    state: DraftState;
    liveData: Record<string, TeamLiveData>;
    valuations: Record<string, TeamValuation>;
  },
  settings: AdvisorSettings,
): Promise<string | null> {
  const { provider, apiKey, model } = settings.llmConfig;
  if (!apiKey) return null;

  const prompt = `You are the elite AI Fantasy Draft Advisor for Commissioner ${context.managerName} in an NFL Fantasy Team Auction draft.
Draft Year: ${context.state.currentDraftYear}.
Roster Cap: ${context.state.rosterCap} teams per manager.
Current Target: ${context.teamAbbr ?? 'Overall Draft Strategy'}.
Current High Bid: $${context.state.auction.highBid} (Leader: ${context.state.auction.highBidderId ?? 'None'}).
Give a sharp, 3-paragraph executive recommendation on whether Rob should bid, max profitable price, and nomination tactics.`;

  if (provider === 'openai') {
    const res = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: model || 'gpt-4o-mini',
        messages: [{ role: 'user', content: prompt }],
        max_tokens: 300,
      }),
    });
    const data = await res.json();
    return data?.choices?.[0]?.message?.content ?? null;
  }

  if (provider === 'openrouter') {
    const res = await fetch('https://openrouter.ai/api/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${apiKey}`,
      },
      body: JSON.stringify({
        model: model || 'anthropic/claude-3.5-haiku',
        messages: [{ role: 'user', content: prompt }],
        max_tokens: 300,
      }),
    });
    const data = await res.json();
    return data?.choices?.[0]?.message?.content ?? null;
  }

  return null;
}
